/**
 * IvtHubClient — IVTHub Smithy TS client with SigV4 auth.
 *
 * Auth flow (mirrors SkillClient):
 *   Federate id_token → Cognito Identity Pool → IAM temp credentials → SigV4
 *
 * Usage:
 *   import { getIvtClient, ListIndustriesCommand } from './IvtHubClient';
 *   const out = await getIvtClient().send(new ListIndustriesCommand({}));
 */

import { IVTHubClient } from '@amzn/ivthub-client';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { HttpRequest } from '@smithy/protocol-http';
import { appConfig } from '../../config';
import { getIdToken } from '../../context/AuthContext';
import { MockIvtHubClient } from '../mocks/mockClient';

// ── Re-export commands & types for convenience ───────────────────────────────

export {
  ListIndustriesCommand,
  GetSolutionMapCommand,
  // Single round-trip for the whole SolutionMap matrix (all industries + their
  // entries), replacing the ListIndustries + N×GetSolutionMap fan-out. See
  // GetSolutionMapAll (CR-282603940).
  GetSolutionMapAllCommand,
  ListAssetsCommand,
  GetAssetCommand,
  CreateAssetCommand,
  UpdateElementCommand,
  UpdateAssetCommand,
  DeleteAssetCommand,
  GetUcPipelineDetailCommand,
  // Unified successor to GetUcPipeline + GetUcPipelineLive — one op that
  // returns per-UC bubbles for either scope (overview = no industry / drill
  // = one industry) with filter params (assetId / pipelineType / snapshot).
  GetPipelineBubblesCommand,
  GetUcFullCommand,
  // UC Catalog write path (merged-asset model): edit a UC's arsenal by
  // linking/unlinking assets + UpsertUcTagById for the SFDC tag. Asset fields
  // themselves go through CreateAssetCommand / UpdateAssetCommand (above).
  LinkUcAssetCommand,
  // Tag write addressed by solution_map.id. The name-addressed UpsertUcTag is
  // @deprecated on the model: it writes `WHERE industry = ? AND use_case = ?` with no
  // status predicate, so it hits every row with that name INCLUDING tombstones (prod
  // still holds a DEPRECATED "Contact Center" carrying its old tag). Not re-exported —
  // nothing in this app may address a tag by name any more.
  UpsertUcTagByIdCommand,
  // SolutionMap write ops (Step 2 — see solution-map-v3-design.md §3)
  UpdateSolutionMapCommand,
  ReorderSolutionMapCommand,
  RenameSolutionMapUcCommand,
  // Atomic column rename (CR-302054976). Replaced a front-end two-loop simulation that
  // re-created every UC under the new name — new ids orphaned uc_asset_link, and
  // priority/notes/status/area_order were all collateral damage.
  RenameSolutionMapAreaCommand,
  // Atomic column soft-delete (CR-302148852), same story: the loop it replaces could
  // half-delete a column, and never rewrote the covered-column lists that cross-area
  // UCs keep in `notes` — so deleting a column left its name in there and the renderer
  // silently fell back to "covers every column".
  DeprecateSolutionMapAreaCommand,
  // Topic ops (SolutionMap "flip side" view — see solution-map-v3-design.md §6)
  ListTopicsCommand,
  UpsertTopicCommand,
  UpsertTopicInstanceCommand,
  DeleteTopicCommand,
  DeleteTopicInstanceCommand,
  // Topic↔Asset link write path (V11): backs Topic-mode asset creation
  // (CreateAsset → LinkTopicAsset) + the New Assets Topic path.
  LinkTopicAssetCommand,
  // Asset Assessment (八要素质量 + Asset 称重 X/Y). GetAssetAssessment reads the
  // per-Asset 1:1 asset_assessment row for the Detail page's Details card. See
  // tech/design/2026-07-09-asset-assessment-design.md §2.3.
  GetAssetAssessmentCommand,
  // Motion domain (V14 motion-centric rewrite — Motion is a first-class entity).
  // Motions list page (ListMotions, DAT-8313) + Create Motion Step1 dialog
  // (CreateMotion, DAT-8315). CreateMotion / ListMotions / DeleteMotion handlers
  // are implemented on mainline (DeleteMotion cascades pool/wave/link in one txn,
  // MotionActivities.deleteMotion). The asset-keyed prototype Motion surface
  // (DefineTargetProfile etc.) was removed in CR-285723348.
  CreateMotionCommand,
  UpdateMotionCommand,
  DeleteMotionPipelineCommand,
  GetMotionCommand,
  ListMotionsCommand,
  // Motion List multi-select → batch delete. DeleteMotion is single-id
  // ({ motionId }); a batch = N parallel calls (no batch op on the backend).
  DeleteMotionCommand,
  SendForTaggingCommand,
  // Pipeline List v2 (DAT-8462): ALL tag-anchored pipeline (no motion claim
  // required). row shape is PipelineListItem (motions[] may be empty for tag-only
  // opps). Backend CR-291680700. hasMotion filter (true/false/omit). Superseded the
  // old ListMotionPipeline op (no longer sent by the frontend).
  ListTaggedPipelinesCommand,
  // Relevance-anchored pipeline (DAT-8634): opportunities ranked by the offline
  // LLM judgment rather than ARR, over a WIDER universe than the tag-anchored
  // list — every opp in the caller's permitted industries, tagged or not. POST
  // with a body. Used by MotionWaveSelectPanel for its default candidate list.
  ListRelevantPipelinesCommand,
  // Pipeline SFDC property-filter candidate values (CR2, DAT-8462): one op serving
  // all 5 fields — SFDC_STAGE whole 7-bucket set; ACCOUNT_NAME/OPP_NAME/OPP_ID/
  // OPP_OWNER ILIKE search + keyset paging (the PropertyFilter dropdown's onLoadItems).
  GetPipelineFilterOptionsCommand,
  // Pipeline row "N 次活动" click → GetOppActivities (CR-285995448).
  GetOppActivitiesCommand,
  // Motion detail page (DAT-8316): GetMotionOverview returns the full overview
  // (Information KPIs + Pipeline List) for a single Motion.
  GetMotionOverviewCommand,
  // Wave curation: claim a mixed batch (EXPLORE + ACCELERATE) into the Motion's
  // work pool. Backend C5 (CR-285988352, merged). Wave is a front-end batching
  // concept — the handler UPSERTs a motion_waves row from the client-provided
  // waveId, no CreateWave call is required.
  ClaimCandidatesCommand,
  // Asset KB file management (A-line, design §4). presigned-URL flow: request an
  // upload URL → browser PUTs bytes straight to S3 → confirm. List / download-url
  // / delete round out CRUD. (Ingest/ready is B-line, not attached yet.)
  // Owner-generic KB read (design 2026-08-03). GetKbTree replaced the flat
  // List*KbFiles reads: one op keyed by (ownerType, ownerId) → folders[] + entries[]
  // + overview. Write ops stay owner-specific until their owner-generic versions land
  // (kbWriteOps dispatches to them).
  GetKbTreeCommand,
  // Overview first-save (owner-generic, 2-step). Subsequent edits reuse the file edit
  // ops. Read comes from GetKbTree.overview.
  RequestKbOverviewUploadUrlCommand,
  ConfirmKbOverviewUploadCommand,
  // Owner-generic KB file WRITE ops (one op per action, keyed by {ownerType, ownerId};
  // kbWriteOps forwards straight through — no per-owner dispatch). These replaced the
  // deprecated per-owner asset/uc write ops. Delete is entry-level (/kb/entries/).
  RequestKbFileUploadUrlCommand,
  ConfirmKbFileUploadCommand,
  RequestKbFileEditUrlCommand,
  ConfirmKbFileEditGenericCommand,
  GetKbFileDownloadUrlGenericCommand,
  DeleteKbEntryCommand,
  // URL entries (a link, not a file — not ingested). Add/Update are URL-specific;
  // delete/move/list reuse DeleteKbEntry / MoveKbEntry / GetKbTree.
  AddKbUrlEntryCommand,
  UpdateKbUrlEntryCommand,
  // Storyline ops (per-industry narrative grouping; storyline-design.md). 8 ops:
  // list/get/create/update(name+assetIds SET)/updateDescription/delete + link/unlink.
  ListStorylinesCommand,
  GetStorylineCommand,
  CreateStorylineCommand,
  UpdateStorylineCommand,
  UpdateStorylineDescriptionCommand,
  DeleteStorylineCommand,
  LinkStorylineAssetCommand,
  UnlinkStorylineAssetCommand,
  // Rule-based permission: the caller's own allowed industries. Backend resolves the
  // identity from the X-Id-Token (no input), returns pre-sorted/split industry codes.
  // permissionClient wraps this (replaced the browser→permission-service direct call).
  GetAllowedIndustriesCommand,
  // KbContentType / KbFileStatus / KbDownloadDisposition are the const-enum idiom
  // (runtime value + same-named type); callers use the VALUE
  // (e.g. KbContentType.PDF), so re-export as values (carries the type too).
  KbContentType,
  KbFileStatus,
  KbDownloadDisposition,
  // Owner-generic KB tree enum (const-enum idiom, value re-export). KbOwnerType =
  // ASSET | UC | STORYLINE (frontend passes uppercase). (entryType is compared as the
  // string literal 'file'/'url' at the callsite, so the KbEntryType enum isn't re-exported.)
  KbOwnerType,
  // PoolType is the same const-enum idiom as AssetOrigin: runtime value +
  // same-named type. Callers use `PoolType.EXPLORE` / `PoolType.ACCELERATE`,
  // so re-export as VALUE (declaration merging carries the type through).
  // AssetOrigin is the TS const-enum idiom: a runtime `const AssetOrigin = {…}`
  // PLUS a same-named `type AssetOrigin`. NewAssetsPage needs the VALUE
  // (AssetOrigin.IVT_INCUBATED) to scope ListAssets to the New Assets slice, so it
  // is re-exported here as a VALUE. This single value re-export ALSO carries the
  // type binding (declaration merging), so it must NOT be repeated in the
  // `export type {}` block below — doing so is a duplicate-identifier error.
  AssetOrigin,
  // ReorderAction / ReorderDirection are same idiom: runtime `const X = {…}` +
  // same-named `type X`. useSolutionMapMutations calls e.g.
  // `ReorderAction.MOVE_UC`, so re-export as VALUE (carries the type too).
  ReorderAction,
  ReorderDirection,
  // OppStatus (LAUNCHED / CLOSED_LOST / ACTIVE): const-enum idiom. OppPoolPanel
  // uses OppStatus.LAUNCHED to decide the terminal-opp badge color, so it needs
  // the VALUE — declaration merging carries the type through.
  OppStatus,
  // Asset GROW (DAT-9116 / DAT-9158): the tag set an asset carries and its Retain review.
  // const-enum idiom — the details card builds its options from the VALUES.
  GrowType,
  RetainReview,
  // SfdcFilterField (SFDC_STAGE / ACCOUNT_NAME / OPP_NAME / OPP_ID / OPP_OWNER):
  // GetPipelineFilterOptions' field param. const-enum idiom — the option-loading
  // hook uses the VALUES (e.g. SfdcFilterField.SFDC_STAGE) to branch. NOTE: this is
  // a DIFFERENT enum from the property-filter tree's field (same 5 members) — this
  // one is the candidate-values op's field; don't conflate.
  SfdcFilterField,
  // Admin configuration — ONE pair for every config; the ConfigurationValue union
  // member says which one. Adding a config adds a union member, not an operation.
  GetConfigurationCommand,
  UpdateConfigurationCommand,
  // NOT admin configuration despite sitting next to it: the sales-hierarchy nodes are SFDC
  // reference data, served ungated, and the configuration page is only their first caller.
  ListHierarchyNodesCommand,
  // Industry onboarding / removal, also on the configuration page. The two enums are
  // VALUE re-exports: the dialogs build their choices and labels from them.
  CreateIndustryCommand,
  GetIndustryReferencesCommand,
  DeleteIndustryCommand,
  // Who may see which industry (DAT-9167), the Access tab. Saved per call, outside the
  // sync-scope revision, so these never conflict with a Publish.
  ListIndustryGrantsCommand,
  CreateIndustryGrantsCommand,
  DeleteIndustryGrantCommand,
  IndustryType,
  IndustryReferenceKind,
  // Value re-export, not just the type: the record-type picker builds its options from
  // Object.values() so the page cannot offer something UpdateConfiguration would reject.
  OpportunityRecordType,
  // Leader Home (GM view) — one call per window returns every industry's flow
  // metrics, its trailing three months and its asset vitality bands.
  GetIndustryPipelineFlowCommand,
  // STO Homepage (DAT-9005). The lighthouse roster and the reclaim block are ONE
  // op: `lighthouse` picks the block, and it also decides whether the window bounds
  // the ACCOUNT universe — a lighthouse account stays on the page reading zero,
  // which is the state the page exists to surface; a reclaim account cannot.
  ListAccountPipelineCommand,
  // Candidate values for that op's `propertyFilter` (DAT-9223): names searched over exactly the
  // block's scope, and the fixed stage buckets. Hierarchy candidates are ListHierarchyNodes'.
  // AccountPipelineFilterField is its `field`, a VALUE re-export like AccountFlag below.
  ListAccountPipelineFilterOptionsCommand,
  AccountPipelineFilterField,
  // The full list behind one bucket cell of the asset table. `bucket` is an INPUT and
  // deliberately not a member of the rows: the buckets are not a partition (OPEN is
  // momentum + base, NOT_FOLLOWED_UP a subset of OPEN), so no one value names a row.
  ListAssetOpportunitiesCommand,
  // "Ignore" / undo on a reclaim row's asset suggestion. Keyed on the (oppty, asset)
  // PAIR, so dismissing one suggestion leaves the row's others standing.
  PutOppAssetDismissalCommand,
  DeleteOppAssetDismissalCommand,
  // Account flags — the wide-table successor to the three *LighthouseAccount* ops
  // whose two tables V86 dropped. Omitting `flag` lists every account carrying ANY
  // flag, NOT every account: a row whose flags are all false is absent.
  ListAccountFlagsCommand,
  // One pair for every flag; the AccountFlag member says which. A new flag is a member
  // plus a column, never a new operation. BIG_BET additionally requires ivthub-admin
  // where LIGHTHOUSE only requires editor — see utils/authorization.ts.
  PutAccountFlagCommand,
  DeleteAccountFlagCommand,
  // The Add Lighthouse Account dialog's candidate search. Its candidates are exactly the
  // accounts PutAccountFlag(LIGHTHOUSE) accepts for this caller — the account's OWN hierarchy
  // in the caller's industries, the write's own check — so a pick does not come back 403.
  SearchAccountsCommand,
  // Const-enum idiom (runtime value + same-named type), same as OppStatus above, so
  // these are VALUE re-exports and must NOT be repeated in `export type {}` below.
  // AccountFlag is the flag writes' discriminator; FlowBucketName is
  // ListAssetOpportunities' required `bucket`.
  AccountFlag,
  FlowBucketName,
  // Leader Home (STO & IS/ISA view, DAT-9005) — the asset-grain sibling of the op
  // above, sharing its window/totals shapes. ANY_MEMBER where the GM op is
  // ADMIN_ONLY, which is why the two views cannot come from one call.
  GetAssetPipelineFlowCommand,
  // Asset Radar — direction surface for the STO Dashboard (DAT-9115, R17). The
  // list op carries the full Figma A/C/D card payload (induction §5.1.1) in one
  // round-trip (direction rows + the three dashboard summary tiles). Get and
  // UpdateStatus serve the detail modal and the FP/adopt flow (R18 / R19) and
  // are re-exported here so the follow-on rows can pick them up without
  // touching this file again. All three are @readonly / @idempotent on the
  // server; the frontend scopes the list call to one industry per request to
  // match the dashboard's industry selector (topic-model §3.3).
  ListRadarDirectionsCommand,
  GetRadarDirectionCommand,
  UpdateRadarDirectionStatusCommand,
  // RadarDirectionStatus / RadarDirectionDismissReason — const-enum idiom
  // (runtime value + same-named type). The STO dashboard filters by
  // RadarDirectionStatus.CANDIDATE and the FP flow (R19) will branch on
  // RadarDirectionDismissReason, so both are re-exported as VALUES (which
  // carry the type through declaration merging). Must NOT be repeated in
  // the `export type {}` block below (duplicate-identifier error).
  RadarDirectionStatus,
  RadarDirectionDismissReason,
  // RadarEvidenceTier / RadarEmptyNewsState / RadarSuggestedOrgSide /
  // RadarNewsFetchMode / RadarNewsSourceKind / RadarOrgAwsCustomerFlag /
  // RadarNeedEvidence — same const-enum idiom. The card renders the
  // evidence tier badge and the news-fetch-mode S/M badges from the enum
  // values (induction §5.1.1), so VALUE re-exports; detail-modal consumers
  // (R18) also read them straight off the server shape.
  RadarEvidenceTier,
  RadarEmptyNewsState,
  RadarSuggestedOrgSide,
  RadarNewsFetchMode,
  RadarNewsSourceKind,
  RadarOrgAwsCustomerFlag,
  RadarNeedEvidence,
  // Radar Admin — the Admin page under /ivt/radar-config (R16). Topic
  // (STANDARD_TOPIC + GENERAL_TOPIC) and news-source CRUD on radar_config
  // (topic-model §3.2); every op is industry-scoped. SetStatus owns the
  // ACTIVE/INACTIVE flip so one lane carries the §2.7 TOPIC_REMOVED
  // direction stamping (no `status` on Upsert draft). The 10 STANDARD_TOPIC
  // cap is handler-enforced; the Admin page mirrors it in the UI.
  ListIndustryTopicsCommand,
  GetIndustryTopicCommand,
  UpsertIndustryTopicCommand,
  SetIndustryTopicStatusCommand,
  GetIndustryGeneralTopicCommand,
  UpsertIndustryGeneralTopicCommand,
  ListIndustryNewsSourcesCommand,
  UpsertIndustryNewsSourceCommand,
  SetIndustryNewsSourceStatusCommand,
  // RadarNewsSourceKindAdmin / RadarNewsSourceLocale — const-enum idiom.
  // The Admin page builds its kind/locale pickers from Object.values(), so
  // VALUE re-exports (declaration merging carries the type); must NOT
  // appear in the `export type {}` block below (duplicate-identifier
  // error). RadarConfigStatus is also const-enum but used only as a type
  // on the server-return shapes, so it lives in the `export type {}` block
  // below with the other Admin shapes.
  RadarNewsSourceKindAdmin,
  RadarNewsSourceLocale,
} from '@amzn/ivthub-client';

export type {
  // Leader Home shapes. FlowMetrics carries six buckets (momentum / base / open /
  // launched / notFollowedUp / lost) — `lost` being the only OPTIONAL one, so absent
  // means "the deployed service predates it", not zero. MonthlyFlowPoint is one month
  // of the trailing series (momentum and launched only) and the list is fixed at three
  // by the contract.
  GetIndustryPipelineFlowOutput,
  // STO view. Carries `window` and `total` of the SAME types as the GM output, so the
  // KPI band takes either one's `total` unchanged. `assets[]` is the later slice.
  GetAssetPipelineFlowOutput,
  IndustryFlowRow,
  FlowMetrics,
  MonthlyFlowPoint,
  AssetVitalityCounts,
  OverallTotals,
  ReportWindow,
  KpiBucket,
  // Admin configuration. SyncScopeConfiguration is the payload behind the
  // 'sync-scope' key.
  ConfigurationValue,
  GetConfigurationOutput,
  UpdateConfigurationOutput,
  SyncScopeConfiguration,
  SyncDateColumnQuery,
  SyncDateToken,
  IndustryNodeMapping,
  IndustryGrants,
  IndustryGrantee,
  HierarchyNode,
  HierarchyLevel,
  SyncDateColumn,
  IndustryMeta,
  SolutionMapEntry,
  ReorderSolutionMapCommandInput,
  AssetSummary,
  // GROW metrics the offline job computes for every asset (null = cannot be computed).
  GrowMetrics,
  AssetDetail,
  UcAssetLink,
  SolutionStatus,
  AssetMaturity,
  ElementStatus,
  ArsenalType,
  ListIndustriesOutput,
  GetSolutionMapOutput,
  GetSolutionMapAllOutput,
  ListAssetsOutput,
  GetAssetOutput,
  GetSolutionMapCommandOutput,
  GetUcPipelineDetailOutput,
  GetPipelineBubblesOutput,
  PipelineBubble,
  StageGroup,
  PipelineOpp,
  Activity,
  UcNode,
  UcAsset,
  // NOTE: AssetOrigin is intentionally NOT here — it is re-exported as a VALUE
  // above (NewAssetsPage uses AssetOrigin.IVT_INCUBATED), and that value export
  // already carries the type via declaration merging. Listing it here too would
  // be a duplicate-identifier error.
  GetUcFullOutput,
  // UC Catalog write path
  UpdateAssetOutput,
  LinkUcAssetOutput,
  UpsertUcTagByIdOutput,
  ManageArsenalOutput,
  // Topic ops
  Topic,
  TopicAgentInput,
  TopicInstance,
  ListTopicsOutput,
  UpsertTopicInput,
  UpsertTopicOutput,
  UpsertTopicInstanceInput,
  UpsertTopicInstanceOutput,
  DeleteTopicInstanceInput,
  TopicAssetLinkRef,
  // SolutionMap write ops
  // Asset Assessment types (八要素质量 + 称重 X/Y + industryRank). See §2.3 of the
  // asset-assessment design doc; V29 shape splits element_scored_at / weighed_at.
  AssetAssessmentDetail,
  GetAssetAssessmentOutput,
  // Motion domain (V14 motion-centric) — ListMotions + CreateMotion + GetMotionOverview.
  MotionListItem,
  MotionOpp,
  // Pipeline List "Topics" column: a Motion's asset→topic links (topicName /
  // subTopicName / subTopicId / industry). NOT TopicAssetLinkRef (that's the
  // write-path link ref for LinkTopicAsset).
  CreateMotionOutput,
  ListMotionsOutput,
  GetMotionOverviewCommandOutput,
  // Pipeline List — tag-anchored (DAT-8462, backend CR-291680700). ListTaggedPipelines
  // fully replaced the old ListMotionPipeline op (last ListMotionPipeline reference,
  // the shared fixture's type, is gone → the whole type family is retired).
  SfdcStageCounts,
  ListTaggedPipelinesOutput,
  PipelineListItem,
  // Pipeline List — relevance-anchored (DAT-8634). One row per OPPORTUNITY
  // carrying the asset that scored it highest; scoreCounts is over the full
  // filtered set and ignores minRelevanceScore, so one call reports both the
  // current threshold's total and what relaxing it would add.
  ListRelevantPipelinesInput,
  ListRelevantPipelinesOutput,
  RelevantPipelineItem,
  RelevanceScoreCounts,
  // GetPipelineFilterOptions (CR2, DAT-8462): SFDC property-filter candidate values.
  GetPipelineFilterOptionsCommandInput,
  // ClaimCandidates (Wave confirm flow). PoolType is a VALUE re-export
  // above (const-enum idiom), so it must NOT appear here.
  Claim,
  GetOppActivitiesOutput,
  MotionOppActivity,
  // KB tree (design 2026-08-03). KbEntry superseded KbFile as the tree leaf (fileId
  // same, filename→displayName, +entryType/folderId/sourceType/url); KbFile is no
  // longer surfaced to the frontend. KbContentType/KbFileStatus/KbDownloadDisposition
  // are VALUE re-exports above (const-enum idiom) — do NOT repeat them here.
  KbEntry,
  KbFolder,
  // Only GetKbTreeOutput is surfaced (a fixture types it); the Input / Command* I/O
  // types aren't re-exported — callers build inputs as inline object literals to the
  // Command constructor (contextually typed), never referencing the standalone type.
  GetKbTreeOutput,
  // Overview first-save (owner-generic). Only the upload-url output is used (fixture);
  // the Input / confirm I/O types are used positionally, not re-exported.
  RequestKbOverviewUploadUrlOutput,
  // Owner-generic file write op I/O (replaced the per-owner asset/uc types). Only the
  // upload-url output is surfaced (mock fixture types it); the rest are used positionally.
  RequestKbFileUploadUrlOutput,
  ConfirmKbFileUploadOutput,
  // Storyline domain (storyline-design.md). Storyline = full projection (GetStoryline
  // / Create / Update out); StorylineSummary = ListStorylines row (+ linkedAssetCount);
  // StorylineAssetLink = the link shape on AssetSummary/AssetDetail.storylineLinks.
  Storyline,
  StorylineSummary,
  ListStorylinesOutput,
  GetStorylineOutput,
  // STO Homepage (DAT-9005). Only the Output types are surfaced — inputs are built as
  // inline object literals to the Command constructor (contextually typed), the way
  // the KB ops do it.
  ListAccountPipelineOutput,
  ListAssetOpportunitiesOutput,
  ListAccountFlagsOutput,
  // The four write ops return an empty body. Typed anyway, so the mock echoes stop
  // compiling the day one of them gains a member.
  PutOppAssetDismissalOutput,
  DeleteOppAssetDismissalOutput,
  PutAccountFlagOutput,
  DeleteAccountFlagOutput,
  // A SearchAccounts candidate. Both names may be absent (display falls back to the id);
  // `isLighthouse` is the global flag, so true may be another industry's doing.
  AccountCandidate,
  SearchAccountsOutput,
  // STO asset-table row shapes. ROWS DO NOT SUM: assets.sfdc_tag has no uniqueness
  // constraint, so two Assets can share a tag and both claim the same opportunity —
  // read `total`, which is computed independently. `monthly` is a trailing
  // three-month series, not a breakdown of the window.
  AssetFlowRow,
  AssetFlowMetrics,
  // namedDeals: the largest few deals behind ONE bucket, for the row's inline list.
  // A preview, not the bucket — ListAssetOpportunities serves the whole bucket, and
  // an empty bucket has no entry at all.
  AssetBucketDeals,
  // Shared item shape: the capped per-bucket preview AND the paginated full list.
  AssetOpportunity,
  // Per-asset form of what AssetVitalityCounts aggregates. Wire members are GREEN /
  // AMBER / RED, not the prototype's ACTIVE / SLOWING / CHURNED labels.
  // (FlowBucketName is ListAssetOpportunities' request-side filter and is a VALUE
  // re-export above — the const-enum idiom carries the type, so it is NOT repeated
  // here; listing it in both blocks is a duplicate-identifier error.)
  AssetVitalityBand,
  // Radar list payload (DAT-9115, R17). ListRadarDirectionsOutput wraps the
  // direction rows and the summary tile struct; RadarDirectionSummary is the
  // card payload, with the Radar(AccountChip|RecentNews|SuggestedOrg|
  // PipelineEvidence) members it composes. The enums are re-exported as
  // VALUES above (declaration merging carries the type), so they must NOT
  // appear here (duplicate-identifier error). Detail-modal (R18) and
  // UpdateStatus (R19) shapes (RadarDirectionDetail, RadarMemberOpp,
  // RadarMemberNews, RadarSuggestedOrgDetail, RadarDirectionNeed,
  // RadarReusableAsset, UpdateRadarDirectionStatusInput /
  // GetRadarDirectionOutput) also surface so their consumers can type the
  // handler shape without re-reading the generated .d.ts.
  ListRadarDirectionsOutput,
  GetRadarDirectionOutput,
  UpdateRadarDirectionStatusInput,
  UpdateRadarDirectionStatusOutput,
  RadarDirectionSummary,
  RadarDirectionDetail,
  RadarDashboardSummary,
  RadarPipelineEvidence,
  RadarAccountChip,
  RadarRecentNews,
  RadarSuggestedOrg,
  RadarSuggestedOrgDetail,
  RadarMemberOpp,
  RadarMemberNews,
  RadarDirectionNeed,
  RadarReusableAsset,
  // Radar Admin shapes (R16). Read-side rows carry status + audit columns;
  // the Draft shapes are the write-side payload (status is NOT on the draft
  // — SetIndustryTopicStatus owns the flip, topic-model §2.7 /
  // SetIndustryNewsSourceStatus for sources). RadarConfigStatus is only
  // used as a type on the read shapes here (ACTIVE / INACTIVE), so it
  // stays in the `type` block even though the generated .d.ts emits it in
  // the const-enum idiom. The *CommandInput types are not re-exported —
  // callers build inputs as inline object literals against the Command
  // constructor (contextually typed), matching the KB / Storyline pattern
  // the rest of this file uses.
  ListIndustryTopicsOutput,
  GetIndustryTopicOutput,
  UpsertIndustryTopicOutput,
  SetIndustryTopicStatusOutput,
  GetIndustryGeneralTopicOutput,
  UpsertIndustryGeneralTopicOutput,
  ListIndustryNewsSourcesOutput,
  UpsertIndustryNewsSourceOutput,
  SetIndustryNewsSourceStatusOutput,
  RadarStandardTopic,
  RadarGeneralTopic,
  RadarNewsSource,
  RadarStandardTopicDraft,
  RadarGeneralTopicDraft,
  RadarNewsSourceDraft,
  RadarConfigStatus,
} from '@amzn/ivthub-client';

// ── Credential provider ──────────────────────────────────────────────────────

let _credentialsProvider:
  | ReturnType<typeof fromCognitoIdentityPool>
  | null = null;

function createCredentialsProvider() {
  if (!_credentialsProvider) {
    _credentialsProvider = fromCognitoIdentityPool({
      identityPoolId: appConfig.identityPoolId,
      logins: {
        [appConfig.identityProvider]: () => {
          const token = getIdToken();
          if (!token) throw new Error('No id_token available');
          return Promise.resolve(token);
        },
      },
      clientConfig: { region: appConfig.region },
    });
  }
  return _credentialsProvider;
}

// ── Client singleton ─────────────────────────────────────────────────────────

let _client: IVTHubClient | null = null;

/**
 * Mock mode — when `VITE_IVT_MOCK=1`, `getIvtClient()` returns a
 * {@link MockIvtHubClient} backed by typed fixtures instead of a live,
 * SigV4-signed client. This lets the frontend be developed and verified WHILE
 * a backend op does not exist yet, or offline without a Midway session. It is
 * also how component tests inject deterministic data. Guarded by `import.meta.env` so it is dead code in a production
 * `vite build` unless the flag is set at build time.
 *
 * The cast is the single deliberate seam: the mock implements only the
 * `send`/`destroy` surface the pages use, while page code keeps the real
 * IVTHubClient type so the Smithy command generics still give typed outputs.
 */
function isMockMode(): boolean {
  return import.meta.env?.VITE_IVT_MOCK === '1' || import.meta.env?.VITE_IVT_MOCK === 'true';
}

/**
 * Throws fast (before caching) if a stage has no IVTHub endpoint configured
 * (a falsy or sentinel `'TBD'` value). All stages now have real endpoints, so
 * this is defensive: without the guard the SDK would try to resolve a bad
 * endpoint as a URL and surface cryptic DNS errors, and the cached singleton
 * would keep the broken endpoint for the rest of the session.
 *
 * In mock mode this guard is skipped — the mock has no endpoint to resolve.
 */
export function getIvtClient(): IVTHubClient {
  if (isMockMode()) {
    if (!_client) _client = new MockIvtHubClient({ latencyMs: 250 }) as unknown as IVTHubClient;
    return _client;
  }
  if (!appConfig.ivtApiEndpoint || appConfig.ivtApiEndpoint === 'TBD') {
    throw new Error(
      `IVT API endpoint is not configured for stage "${appConfig.stage}". ` +
        `IVTHub backend has not been onboarded to this stage yet.`,
    );
  }
  if (!_client) {
    _client = new IVTHubClient({
      endpoint: appConfig.ivtApiEndpoint,
      region: appConfig.region,
      credentials: createCredentialsProvider(),
    });
    // Layer 2 per-user authz (DAT-8256): inject the user's Federate id_token as
    // X-Id-Token on every request so the backend can check `ldap_groups` against
    // the IVT groups — membership admits, and `ivthub-editor` / `ivthub-admin`
    // additionally carry the write role (DAT-8911; see ivt/utils/authorization.ts).
    // Authorization is taken by SigV4, so the id_token rides a separate header.
    // The `build` step runs BEFORE the SigV4 signing step, so this header is
    // covered by the signature (tamper-proof).
    _client.middlewareStack.add(
      (next) => async (args) => {
        if (HttpRequest.isInstance(args.request)) {
          const token = getIdToken();
          if (token) args.request.headers['X-Id-Token'] = token;
        }
        return next(args);
      },
      { step: 'build', name: 'ivtIdTokenHeader', priority: 'high' },
    );
  }
  return _client;
}

/**
 * Inject a client directly — for tests that want full control over responses
 * (e.g. forcing an error state). Pass `null` via {@link resetIvtClient} to clear.
 */
export function __setIvtClientForTesting(client: unknown | null): void {
  _client = client as IVTHubClient | null;
}

/**
 * Reset the client (e.g. after re-login when credentials expire).
 * Next call to getIvtClient() will create a fresh instance.
 */
export function resetIvtClient(): void {
  if (_client) {
    _client.destroy();
    _client = null;
  }
}
